/* prettier-ignore-file */
/**
 * Full-screen landscape signature capture, for phones only.
 *
 * A signature drawn in a short, narrow box on a phone is cramped and unrepresentative
 * of how someone actually signs — and this is a legal artifact. This gives the full
 * screen, in landscape, which is the natural aspect for a signature.
 *
 * WHY A ROTATE PROMPT RATHER THAN A CSS ROTATION:
 * rotating the canvas with a CSS transform would break drawing outright —
 * SignaturePad maps pointer coordinates via getBoundingClientRect(), which reports the
 * rotated bounding box, so strokes would land nowhere near the finger. Asking for a
 * real device rotation keeps the pointer maths honest.
 *
 * The pad is deliberately NOT mounted until the device is actually in landscape.
 * SignaturePad rebuilds its canvas when its dimensions change, which wipes the
 * drawing — so mounting in portrait would mean a half-drawn signature vanishing the
 * moment the user turns the phone.
 */

import React, { useEffect, useState } from "react";
import { View, Text, StyleSheet, TouchableOpacity, Modal } from "react-native";
import SignaturePad from "./SignaturePad";
import { colors } from "../theme";

const SignatureCaptureModal = ({ visible, onCancel, onConfirm, title = "Sign here" }) => {
  const [signature, setSignature] = useState(null);
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  // Start clean every time it opens, so a previous attempt is never silently reused.
  useEffect(() => {
    if (visible) setSignature(null);
  }, [visible]);

  // Best-effort orientation lock. Only works in fullscreen on some Android browsers
  // and is unsupported on iOS Safari, hence the manual prompt as the real mechanism.
  useEffect(() => {
    if (!visible) return undefined;
    const lock = window.screen?.orientation?.lock;
    if (typeof lock === "function") {
      window.screen.orientation.lock("landscape").catch(() => {
        /* unsupported or refused — the rotate prompt covers it */
      });
    }
    return () => {
      try {
        window.screen?.orientation?.unlock?.();
      } catch (error) {
        /* nothing to undo */
      }
    };
  }, [visible]);

  const isLandscape = viewport.width > viewport.height;
  // Leave room for the header and the button row.
  const padWidth = Math.max(240, viewport.width - 32);
  const padHeight = Math.max(160, viewport.height - 150);

  return (
    <Modal visible={visible} animationType="slide" onRequestClose={onCancel}>
      <View style={styles.container}>
        {!isLandscape ? (
          <View style={styles.rotatePrompt}>
            <Text style={styles.rotateIcon}>📱↻</Text>
            <Text style={styles.rotateTitle}>Turn your phone sideways</Text>
            <Text style={styles.rotateText}>
              Rotate to landscape for a full-width signing area.
            </Text>
            <TouchableOpacity
              dataSet={{ hover: "lift" }}
              style={[styles.button, styles.cancelButton, styles.rotateCancel]}
              onPress={onCancel}
            >
              <Text style={styles.cancelText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <>
            <View style={styles.header}>
              <Text style={styles.title}>{title}</Text>
              <Text style={styles.hint}>Use your finger</Text>
            </View>

            <View style={styles.padWrapper}>
              <SignaturePad
                width={padWidth}
                height={padHeight}
                isEmpty={!signature}
                onSignatureChange={setSignature}
              />
            </View>

            <View style={styles.buttonRow}>
              <TouchableOpacity
                dataSet={{ hover: "lift" }}
                style={[styles.button, styles.cancelButton]}
                onPress={onCancel}
              >
                <Text style={styles.cancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                dataSet={{ hover: "lift" }}
                style={[styles.button, styles.confirmButton, !signature && styles.confirmDisabled]}
                onPress={() => onConfirm(signature)}
                disabled={!signature}
              >
                <Text style={[styles.confirmText, !signature && styles.confirmTextDisabled]}>
                  Use this signature
                </Text>
              </TouchableOpacity>
            </View>
          </>
        )}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.bg, padding: 16 },

  rotatePrompt: { flex: 1, alignItems: "center", justifyContent: "center", padding: 24 },
  rotateIcon: { fontSize: 44, marginBottom: 14 },
  rotateTitle: { fontSize: 19, fontWeight: "700", color: colors.ink900, textAlign: "center" },
  rotateText: {
    fontSize: 14,
    color: colors.ink500,
    textAlign: "center",
    marginTop: 8,
    lineHeight: 20,
  },
  rotateCancel: { marginTop: 28, minWidth: 160 },

  header: { flexDirection: "row", alignItems: "baseline", justifyContent: "space-between" },
  title: { fontSize: 17, fontWeight: "700", color: colors.ink900 },
  hint: { fontSize: 12, color: colors.ink400 },

  padWrapper: { flex: 1, justifyContent: "center", paddingVertical: 8 },

  buttonRow: { flexDirection: "row" },
  button: {
    flex: 1,
    borderRadius: 8,
    paddingVertical: 13,
    minHeight: 48,
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  cancelButton: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    marginRight: 8,
  },
  cancelText: { fontSize: 15, fontWeight: "600", color: colors.ink700 },
  confirmButton: { backgroundColor: colors.primary, marginLeft: 8 },
  confirmDisabled: { backgroundColor: colors.disabled, cursor: "default" },
  confirmText: { fontSize: 15, fontWeight: "600", color: colors.surface },
  confirmTextDisabled: { color: colors.ink400 },
});

export default SignatureCaptureModal;
